
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router';
import {
  FilterBar,
  FilterState,
  PrayerList,
  RespondModal,
} from '@/components/moderation';
import { RecordingConsentModal } from '@/components/moderation/RecordingConsentModal';
import { MyRecordingConsentModal } from '@/components/moderation/MyRecordingConsentModal';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button, Select } from '@/components/ui';
import { useAuth } from '@/lib/auth-context';
import { useBranding } from '@/lib/branding-context';
import { api } from '@/lib/api';
import { usePrayerRequests } from '@/hooks/usePrayerRequests';
import { PendingDigestFrequency, PrayerRequest, PrayerStatus } from '@/types';

export default function PrayerPartnerPage() {
  const [searchParams] = useSearchParams();
  const { branding } = useBranding();
  const responsesEnabled = branding.responsesEnabled !== false;

  const [token, setToken] = useState<string | undefined>(undefined);
  const [respondingTo, setRespondingTo] = useState<PrayerRequest | null>(null);

  const initialStatus = searchParams.get('status') as PrayerStatus | null;
  const initialVideo = searchParams.get('video_response_status') as
    | 'awaiting'
    | 'responded'
    | null;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedToken = localStorage.getItem('access_token');
      if (storedToken) {
        setToken(storedToken);
      }
    }
  }, []);

  const initialFilters: Partial<FilterState> = useMemo(
    () => ({
      status: initialStatus ?? 'all',
      videoResponseStatus: initialVideo ?? 'awaiting',
      sortOrder: 'asc',
    }),
    [initialStatus, initialVideo]
  );

  const { user, refreshUser } = useAuth();
  const [showConsent, setShowConsent] = useState(false);
  const [consentOpenedFromRespond, setConsentOpenedFromRespond] = useState(false);
  const [showMyConsent, setShowMyConsent] = useState(false);
  const [digestSaving, setDigestSaving] = useState(false);
  const hasRecordingPermission = Boolean(user?.canRespondVideo || user?.canRespondAudio);
  const digestFrequency: PendingDigestFrequency =
    user?.pendingDigestFrequency ?? 'daily';

  const handleDigestFrequencyChange = useCallback(
    async (value: string) => {
      const next = value as PendingDigestFrequency;
      if (next === digestFrequency || digestSaving) return;
      setDigestSaving(true);
      try {
        const { error } = await api.patch('/me/pending-digest-frequency', {
          pendingDigestFrequency: next,
        });
        if (!error) {
          await refreshUser();
        }
      } finally {
        setDigestSaving(false);
      }
    },
    [digestFrequency, digestSaving, refreshUser]
  );

  // Reload permissions and consent status when opening this page (e.g. after admin updates).
  useEffect(() => {
    void refreshUser();
  }, [refreshUser]);

  useEffect(() => {
    if (user?.recordingConsentRequired && !user?.recordingConsentRevoked) {
      setConsentOpenedFromRespond(false);
      setShowConsent(true);
    } else if (!consentOpenedFromRespond) {
      setShowConsent(false);
    }
  }, [user?.recordingConsentRequired, user?.recordingConsentRevoked, consentOpenedFromRespond]);
  const currentUserId = user?.id ?? null;

  const {
    prayers,
    total,
    page,
    totalPages,
    isLoading,
    error,
    filters,
    setFilters,
    setPage,
    refresh,
    editingBy,
    respondingBy,
    sendMessage,
  } = usePrayerRequests({
    initialFilters,
    pageSize: 20,
    token,
    autoFetch: responsesEnabled,
    enableRealtime: responsesEnabled,
  });

  const respondingToPrayerIdRef = useRef<string | null>(null);

  const handleRespond = useCallback(
    async (prayer: PrayerRequest) => {
      const freshUser = await refreshUser();
      if (freshUser?.recordingConsentRequired) {
        setConsentOpenedFromRespond(true);
        setShowConsent(true);
        return;
      }
      setRespondingTo(prayer);
      respondingToPrayerIdRef.current = prayer.id;
      sendMessage({ action: 'start_responding', prayer_id: prayer.id });
    },
    [sendMessage, refreshUser]
  );

  const handleFilterChange = useCallback(
    (newFilters: FilterState) => {
      setFilters(newFilters);
    },
    [setFilters]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setPage]
  );

  if (!responsesEnabled) {
    return (
      <div className="mx-auto max-w-7xl space-y-6">
        <PageHeader
          title="Responses"
          description="Partner responses are turned off for this organization."
        />
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-800">
          <p className="text-slate-600 dark:text-slate-300">
            An administrator has disabled the responses feature. Existing
            consents and past responses are kept. Ask an admin to turn responses
            back on under Settings → Responses.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mx-auto max-w-7xl">
        <PageHeader
          title="Responses"
          description={`${total} total • Page ${page} of ${totalPages || 1}`}
          actions={
            <>
            {hasRecordingPermission && (
              <div className="flex items-center gap-2">
                <span className="whitespace-nowrap text-sm text-slate-600 dark:text-slate-400">
                  Email reminders
                </span>
                <div className="w-28">
                  <Select
                    aria-label="Email reminders"
                    value={digestFrequency}
                    disabled={digestSaving}
                    onChange={(e) => void handleDigestFrequencyChange(e.target.value)}
                    className="py-2.5 text-sm"
                    options={[
                      { value: 'off', label: 'Off' },
                      { value: 'daily', label: 'Daily' },
                      { value: 'weekly', label: 'Weekly' },
                    ]}
                  />
                </div>
              </div>
            )}
            {hasRecordingPermission && (
              <Button variant="outline" onClick={() => setShowMyConsent(true)}>
                My consent
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => refresh()}
              disabled={isLoading}
            >
              <svg
                className="mr-2 h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh
            </Button>
            </>
          }
        />

        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-900/20">
            <div className="flex items-center gap-2">
              <svg
                className="h-5 w-5 text-red-600 dark:text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-sm font-medium text-red-800 dark:text-red-200">
                {error}
              </p>
            </div>
          </div>
        )}

        <div className="mb-6">
          <FilterBar
            filters={filters}
            onFilterChange={handleFilterChange}
            isLoading={isLoading}
          />
        </div>

        <PrayerList
          prayers={prayers}
          isLoading={isLoading}
          onEdit={() => {}}
          onQueue={() => {}}
          onDismiss={() => {}}
          onRespond={handleRespond}
          listContext="partner"
          canRespondVideo={user?.canRespondVideo}
          canRespondAudio={user?.canRespondAudio}
          userRole={user?.role}
          editingBy={editingBy}
          respondingBy={respondingBy}
          currentUserId={currentUserId}
        />

        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-6 dark:border-slate-700">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Showing {(page - 1) * 20 + 1} to {Math.min(page * 20, total)} of{' '}
              {total} results
            </p>
            <nav className="flex items-center gap-2" aria-label="Pagination">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1 || isLoading}
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                <span className="sr-only sm:not-sr-only sm:ml-1">Previous</span>
              </Button>

              <span className="text-sm text-slate-600 dark:text-slate-400">
                {page} / {totalPages}
              </span>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages || isLoading}
              >
                <span className="sr-only sm:not-sr-only sm:mr-1">Next</span>
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Button>
            </nav>
          </div>
        )}

        {user && (
          <>
            <RecordingConsentModal
              isOpen={showConsent}
              user={user}
              dismissible={Boolean(user.recordingConsentRevoked || consentOpenedFromRespond)}
              onClose={() => {
                setShowConsent(false);
                setConsentOpenedFromRespond(false);
              }}
              onComplete={async () => {
                await refreshUser();
                setShowConsent(false);
                setConsentOpenedFromRespond(false);
              }}
            />
            <MyRecordingConsentModal
              isOpen={showMyConsent}
              user={user}
              onClose={() => setShowMyConsent(false)}
              onUpdated={async () => {
                await refreshUser();
              }}
            />
          </>
        )}

        <RespondModal
          prayer={respondingTo}
          isOpen={!!respondingTo && !user?.recordingConsentRequired}
          canRespondVideo={user?.canRespondVideo}
          canRespondAudio={user?.canRespondAudio}
          onClose={() => {
            const prayerId = respondingToPrayerIdRef.current;
            if (prayerId)
              sendMessage({ action: 'stop_responding', prayer_id: prayerId });
            respondingToPrayerIdRef.current = null;
            setRespondingTo(null);
          }}
          onSuccess={() => {
            const prayerId = respondingToPrayerIdRef.current;
            if (prayerId)
              sendMessage({ action: 'stop_responding', prayer_id: prayerId });
            respondingToPrayerIdRef.current = null;
            setRespondingTo(null);
            refresh();
          }}
        />
      </div>
    </div>
  );
}
